import { type ISelectOption } from 'app/interfaces/ISelectOption';
import { compressionCodecType, compressionCodecTypeLabel, Encoding } from 'shell-models-lib';

// some formats are not supported, but kept as in ADF for backward compatibility & are removed in Trident
function filterFormatTypes(arr: Array<ISelectOption<string>>, formats: string[]): Array<ISelectOption<string>> {
    return arr.filter((opt) => !formats.includes(opt.value) || !__IS_FABRIC_DPE__);
}

// newly added in v2
export const parquetV2Options = [
    {
        label: compressionCodecTypeLabel.brotli,
        value: compressionCodecType.brotli
    },
    {
        label: compressionCodecTypeLabel.zstd,
        value: compressionCodecType.zstd
    },
    {
        label: compressionCodecTypeLabel.lz4,
        value: compressionCodecType.lz4
    },
    {
        label: compressionCodecTypeLabel.lz4hadoop,
        value: compressionCodecType.lz4hadoop
    }
];

// avro doesn't support snappy
export const avroCompressionTypeOptions: Array<
    ISelectOption<string>
> = filterFormatTypes([
    {
        label: compressionCodecTypeLabel.deflate,
        value: compressionCodecType.deflate
    },
    {
        label: compressionCodecTypeLabel.snappy,
        value: compressionCodecType.snappy
    }
], [compressionCodecType.snappy]);

// json doesn't support snappy & lz4, this is also shared by xml
export const jsonCompressionTypeOptions: Array<
    ISelectOption<string>
> = filterFormatTypes([
    {
        label: compressionCodecTypeLabel.bzip2,
        value: compressionCodecType.bzip2
    },
    {
        label: compressionCodecTypeLabel.gzip,
        value: compressionCodecType.gzip
    },
    {
        label: compressionCodecTypeLabel.deflate,
        value: compressionCodecType.deflate
    },
    {
        label: compressionCodecTypeLabel.ZipDeflate,
        value: compressionCodecType.ZipDeflate
    },
    {
        label: compressionCodecTypeLabel.snappy,
        value: compressionCodecType.snappy
    },
    {
        label: compressionCodecTypeLabel.lz4,
        value: compressionCodecType.lz4
    },
    {
        label: compressionCodecTypeLabel.TarGZip,
        value: compressionCodecType.TarGZip
    },
    {
        label: compressionCodecTypeLabel.tar,
        value: compressionCodecType.tar
    }
], [compressionCodecType.snappy, compressionCodecType.lz4]);

// delimited doesn't support snappy & lz4
export const delimitedTextCompressionTypeOptions: Array<
    ISelectOption<string>
> = [
        {
            label: compressionCodecTypeLabel.bzip2,
            value: compressionCodecType.bzip2
        },
        {
            label: compressionCodecTypeLabel.gzip,
            value: compressionCodecType.gzip
        },
        {
            label: compressionCodecTypeLabel.deflate,
            value: compressionCodecType.deflate
        },
        {
            label: compressionCodecTypeLabel.ZipDeflate,
            value: compressionCodecType.ZipDeflate
        },
        {
            label: compressionCodecTypeLabel.TarGZip,
            value: compressionCodecType.TarGZip
        },
        {
            label: compressionCodecTypeLabel.tar,
            value: compressionCodecType.tar
        }
    ];

export const parquetCompressionTypeOptions: Array<ISelectOption<string>> = filterFormatTypes([
    {
        label: stringResources.None,
        value: compressionCodecType.none
    },
    {
        label: compressionCodecTypeLabel.gzip,
        value: compressionCodecType.gzip
    },
    {
        label: compressionCodecTypeLabel.snappy,
        value: compressionCodecType.snappy
    },
    {
        label: compressionCodecTypeLabel.lzo,
        value: compressionCodecType.lzo
    }
], [compressionCodecType.lzo]);

export const parquetV2CompressionTypeOptions = parquetCompressionTypeOptions.concat(parquetV2Options);

// orc doesn't support lzo
export const orcCompressionTypeOptions: Array<ISelectOption<string>> = filterFormatTypes([
    {
        label: stringResources.None,
        value: compressionCodecType.none
    },
    {
        label: compressionCodecTypeLabel.zlib,
        value: compressionCodecType.zlib
    },
    {
        label: compressionCodecTypeLabel.snappy,
        value: compressionCodecType.snappy
    },
    {
        label: compressionCodecTypeLabel.lzo,
        value: compressionCodecType.lzo
    }
], [compressionCodecType.lzo]);

// kept as is for now, as trident delta doesn't show compression options
export const deltaCompressionTypeOptions: Array<ISelectOption<string>> = [
    {
        label: stringResources.None,
        value: compressionCodecType.none
    },
    {
        label: compressionCodecTypeLabel.gzip,
        value: compressionCodecType.gzip
    },
    {
        label: compressionCodecTypeLabel.snappy,
        value: compressionCodecType.snappy
    }
];

export const deltaV2CompressionTypeOptions = deltaCompressionTypeOptions.concat(parquetV2Options);

export const avroCompressionLevelOptions: Array<ISelectOption<number>> = ((Array.apply(null, { length: 9 }) as undefined[]).map((_, index) => ({
    label: `${index + 1}`,
    value: index + 1
})));

export const encodingNameOptions: Array<ISelectOption<string>> = [
    {
        label: stringResources.Default + '(UTF-8)',
        value: undefined
    },
    {
        label: 'UTF-7',
        value: 'UTF-7'
    },
    {
        label: 'UTF-8',
        value: 'UTF-8'
    },
    {
        label: Encoding.UTF_8_WITHOUT_BOM,
        value: Encoding.UTF_8_WITHOUT_BOM
    },
    {
        label: 'UTF-16LE',
        value: 'UTF-16LE'
    },
    {
        label: 'UTF-16BE',
        value: 'UTF-16BE'
    },
    {
        label: 'UTF-32LE',
        value: 'UTF-32LE'
    },
    {
        label: 'UTF-32BE',
        value: 'UTF-32BE'
    },
    {
        label: 'US-ASCII',
        value: 'US-ASCII'
    },
    {
        label: 'BIG5',
        value: 'BIG5'
    },
    {
        label: 'EUC-JP',
        value: 'EUC-JP'
    },
    {
        label: 'EUC-KR',
        value: 'EUC-KR'
    },
    {
        label: 'GB2312',
        value: 'GB2312'
    },
    {
        label: 'GB18030',
        value: 'GB18030'
    },
    {
        label: 'JOHAB',
        value: 'JOHAB'
    },
    {
        label: 'SHIFT-JIS',
        value: 'SHIFT-JIS'
    },
    {
        label: 'CP875',
        value: 'CP875'
    },
    {
        label: 'CP866',
        value: 'CP866'
    },
    {
        label: 'IBM00858',
        value: 'IBM00858'
    },
    {
        label: 'IBM037',
        value: 'IBM037'
    },
    {
        label: 'IBM273',
        value: 'IBM273'
    },
    {
        label: 'IBM437',
        value: 'IBM437'
    },
    {
        label: 'IBM500',
        value: 'IBM500'
    },
    {
        label: 'IBM737',
        value: 'IBM737'
    },
    {
        label: 'IBM775',
        value: 'IBM775'
    },
    {
        label: 'IBM850',
        value: 'IBM850'
    },
    {
        label: 'IBM852',
        value: 'IBM852'
    },
    {
        label: 'IBM855',
        value: 'IBM855'
    },
    {
        label: 'IBM857',
        value: 'IBM857'
    },
    {
        label: 'IBM860',
        value: 'IBM860'
    },
    {
        label: 'IBM861',
        value: 'IBM861'
    },
    {
        label: 'IBM863',
        value: 'IBM863'
    },
    {
        label: 'IBM864',
        value: 'IBM864'
    },
    {
        label: 'IBM865',
        value: 'IBM865'
    },
    {
        label: 'IBM869',
        value: 'IBM869'
    },
    {
        label: 'IBM870',
        value: 'IBM870'
    },
    {
        label: 'IBM01140',
        value: 'IBM01140'
    },
    {
        label: 'IBM01141',
        value: 'IBM01141'
    },
    {
        label: 'IBM01142',
        value: 'IBM01142'
    },
    {
        label: 'IBM01143',
        value: 'IBM01143'
    },
    {
        label: 'IBM01144',
        value: 'IBM01144'
    },
    {
        label: 'IBM01145',
        value: 'IBM01145'
    },
    {
        label: 'IBM01146',
        value: 'IBM01146'
    },
    {
        label: 'IBM01147',
        value: 'IBM01147'
    },
    {
        label: 'IBM01148',
        value: 'IBM01148'
    },
    {
        label: 'IBM01149',
        value: 'IBM01149'
    },
    {
        label: 'ISO-2022-JP',
        value: 'ISO-2022-JP'
    },
    {
        label: 'ISO-2022-KR',
        value: 'ISO-2022-KR'
    },
    {
        label: 'ISO-8859-1',
        value: 'ISO-8859-1'
    },
    {
        label: 'ISO-8859-2',
        value: 'ISO-8859-2'
    },
    {
        label: 'ISO-8859-3',
        value: 'ISO-8859-3'
    },
    {
        label: 'ISO-8859-4',
        value: 'ISO-8859-4'
    },
    {
        label: 'ISO-8859-5',
        value: 'ISO-8859-5'
    },
    {
        label: 'ISO-8859-6',
        value: 'ISO-8859-6'
    },
    {
        label: 'ISO-8859-7',
        value: 'ISO-8859-7'
    },
    {
        label: 'ISO-8859-8',
        value: 'ISO-8859-8'
    },
    {
        label: 'ISO-8859-9',
        value: 'ISO-8859-9'
    },
    {
        label: 'ISO-8859-13',
        value: 'ISO-8859-13'
    },
    {
        label: 'ISO-8859-15',
        value: 'ISO-8859-15'
    },
    {
        label: 'WINDOWS-874',
        value: 'WINDOWS-874'
    },
    {
        label: 'WINDOWS-1250',
        value: 'WINDOWS-1250'
    },
    {
        label: 'WINDOWS-1251',
        value: 'WINDOWS-1251'
    },
    {
        label: 'WINDOWS-1252',
        value: 'WINDOWS-1252'
    },
    {
        label: 'WINDOWS-1253',
        value: 'WINDOWS-1253'
    },
    {
        label: 'WINDOWS-1254',
        value: 'WINDOWS-1254'
    },
    {
        label: 'WINDOWS-1255',
        value: 'WINDOWS-1255'
    },
    {
        label: 'WINDOWS-1256',
        value: 'WINDOWS-1256'
    },
    {
        label: 'WINDOWS-1257',
        value: 'WINDOWS-1257'
    },
    {
        label: 'WINDOWS-1258',
        value: 'WINDOWS-1258'
    }
];

export const delimitedTextEncodingNameOptions = encodingNameOptions;

export const jsonEncodingNameOptions = encodingNameOptions;

export const xmlEncodingNameOptions = encodingNameOptions.filter((option) => option.value !== Encoding.UTF_8_WITHOUT_BOM);

export const jsonTextEncodingNameOptionsForDataflow = encodingNameOptions.filter((option) => option.value !== Encoding.UTF_8_WITHOUT_BOM);

export const xmlTextEncodingNameOptionsForDataflow = encodingNameOptions.filter((option) => option.value !== Encoding.UTF_8_WITHOUT_BOM);

export const delimitedTextEncodingNameOptionsForDataflow = encodingNameOptions.filter((option) => option.value !== Encoding.UTF_8_WITHOUT_BOM && option.value !== Encoding.UTF_7);

export const cdmEncodingNameOptionsForDataflow = encodingNameOptions.filter((option) => option.value !== Encoding.UTF_8_WITHOUT_BOM && option.value !== Encoding.UTF_7);
